// src/components/BundleBuilder.js
// ─────────────────────────────────────────────────────────────
//  Admin editor for BUNDLE menu items — add components (e.g. "Pizza 1",
//  "Pizza 2", "Pizza 3"), each REFERENCING a reusable ModifierTemplate
//  (defined once in the Templates tab) instead of owning bespoke
//  modifier groups. "Copy from above" makes giving every pizza slot the
//  same template a single click.
// ─────────────────────────────────────────────────────────────
import React, { useState, useEffect } from 'react';
import api from '../services/api';
import TemplateEditor from './TemplateEditor';

const EMPTY_COMPONENT = () => ({ displayName: '', templateId: '' });

export default function BundleBuilder({ components, onChange }) {
  const list = components || [];
  const [templates,   setTemplates]   = useState([]);
  const [loading,      setLoading]    = useState(true);
  const [editingTplId, setEditingTplId] = useState(null); // templateId being edited inline, or null

  const loadTemplates = () => {
    setLoading(true);
    api.get('/vendors/me/templates')
      .then(r => setTemplates(r.data || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => { loadTemplates(); }, []);

  const addComponent    = () => onChange([...list, EMPTY_COMPONENT()]);
  const removeComponent = (ci) => onChange(list.filter((_, i) => i !== ci));
  const moveComponent   = (ci, dir) => {
    const ni = ci + dir;
    if (ni < 0 || ni >= list.length) return;
    const next = [...list];
    [next[ci], next[ni]] = [next[ni], next[ci]];
    onChange(next);
  };
  const updateComponent = (ci, patch) => {
    onChange(list.map((c, i) => i === ci ? { ...c, ...patch } : c));
  };
  const copyFromAbove = (ci) => {
    if (ci === 0) return;
    updateComponent(ci, { templateId: list[ci - 1].templateId });
  };

  const templateById = (id) => templates.find(t => t.id === id);

  return (
    <div>
      <div style={{ fontSize:12, color:'var(--text2)', marginBottom:12 }}>
        A bundle is made of components a customer customizes one at a time (e.g. "Pizza 1", "Pizza 2", "Pizza 3").
        Each component points at a reusable <strong>Customization Template</strong> (managed in the 🗂 Templates tab) —
        editing a template updates every bundle that uses it.
      </div>

      {templates.length === 0 && !loading && (
        <div style={{ padding:'12px 16px', background:'#3d2e10', border:'1px solid var(--accent)', borderRadius:10, marginBottom:14, fontSize:13, color:'var(--accent)' }}>
          ⚠ No templates yet. Go to the 🗂 Templates tab to create one (or copy from an existing item) before adding components here.
        </div>
      )}

      {list.map((comp, ci) => {
        const tpl = templateById(comp.templateId);
        return (
          <div key={ci} className="card" style={{ marginBottom:14, border:'1px solid var(--accent)' }}>
            <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:12 }}>
              <span style={{ fontWeight:700, color:'var(--accent)' }}>Component {ci + 1}</span>
              <input type="text" value={comp.displayName} placeholder='e.g. "Pizza 1"'
                onChange={e => updateComponent(ci, { displayName: e.target.value })}
                style={{ flex:1 }} />
              <button className="btn btn-ghost btn-sm" disabled={ci === 0} onClick={() => moveComponent(ci, -1)}>↑</button>
              <button className="btn btn-ghost btn-sm" disabled={ci === list.length - 1} onClick={() => moveComponent(ci, 1)}>↓</button>
              <button className="btn btn-danger btn-sm" onClick={() => removeComponent(ci)}>Remove</button>
            </div>

            <div style={{ display:'flex', gap:8, alignItems:'center', marginBottom:8 }}>
              <select value={comp.templateId || ''} style={{ flex:1 }}
                onChange={e => updateComponent(ci, { templateId: e.target.value })}>
                <option value="">— Select a customization template —</option>
                {templates.map(t => (
                  <option key={t.id} value={t.id}>{t.name} ({t.groups?.length || 0} groups)</option>
                ))}
              </select>
              {ci > 0 && (
                <button className="btn btn-ghost btn-sm" onClick={() => copyFromAbove(ci)}>
                  ↳ Copy from above
                </button>
              )}
            </div>

            {tpl && (
              <div style={{ fontSize:12, color:'var(--text2)', padding:'10px 12px', background:'var(--bg3)', borderRadius:8, marginBottom:6 }}>
                <strong>{tpl.name}</strong>: {(tpl.groups || []).map(g =>
                  `${g.name} (${g.required ? 'req' : 'optional'}, pick ${g.minSelect === g.maxSelect ? g.minSelect : `${g.minSelect}-${g.maxSelect === 99 ? '∞' : g.maxSelect}`})`
                ).join(' · ') || 'no groups in this template'}
                <button className="btn btn-ghost btn-sm" style={{ marginLeft:10 }} onClick={() => setEditingTplId(tpl.id)}>
                  Edit this template
                </button>
              </div>
            )}
          </div>
        );
      })}

      <button className="btn btn-primary" style={{ width:'100%', padding:13 }} onClick={addComponent}>
        + Add Component
      </button>

      {editingTplId && (
        <TemplateEditor
          template={templateById(editingTplId)}
          usage={[]}
          loading={false}
          onCancel={() => setEditingTplId(null)}
          onSave={async ({ id, name, groups }) => {
            try {
              await api.patch(`/vendors/me/templates/${id}`, { name, groups });
            } catch (_) { /* best-effort — bundle builder isn't the place to surface this error in detail */ }
            setEditingTplId(null);
            loadTemplates();
          }}
        />
      )}
    </div>
  );
}
